import ButtonLink from "@affinity/components/ButtonLink";
import { DeliveryCards } from "@affinity/components/DeliveryCard";
import { Divider } from "@affinity/components/Divider";
import OrderQuantity from "@affinity/components/OrderQuantity/OrderQuantity";
import { Section } from "@affinity/components/Section";
import { toDate } from "@affinity/constants/FormatHelpers";
import { FlexWrapEnd, textIconButton } from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { rem } from "@affinity/constants/StyleHelpers";
import SetEmailQuote from "@affinity/Handlers/EmailQuote";
import GetQuoteGetPricing from "@affinity/Handlers/GetQuoteGetPricing";
import SetQuoteMainDetails from "@affinity/Handlers/SetQuoteMainDetails";
import { CustomState } from "@affinity/Store/Reducers";
import { AffinityDelivery } from "@affinity/Store/StoreTypes";
import { AffinityProduct } from "@affinity/Store/StoreTypes";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import RadioGroup from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

let timeOutTrack: ReturnType<typeof setTimeout>;

const mapStateToProps = (state: CustomState) => {
    // Map required state elements to component properties
    const quantityRegular = state.affinity_get_quote.quantityRegular;
    const quantityPremium = state.affinity_get_quote.quantityPremium;
    const productRegular = state.affinity_get_quote.productRegular;
    const productPremium = state.affinity_get_quote.productPremium;
    const postcode = state.affinity_get_quote.postcode;
    const deliveryCheapest = state.affinity_get_quote.deliveryCheapest;
    const deliverySoonest = state.affinity_get_quote.deliverySoonest;
    const hasAttempted = state.affinity_get_quote.hasAttemped;
    return {
        quantityRegular,
        quantityPremium,
        postcode,
        deliveryCheapest,
        hasAttempted,
        deliverySoonest,
        productRegular,
        productPremium,
        formState: state.affinity_get_quote,
    };
};

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    SetEmailQuote,
    SetQuoteMainDetails,
    GetQuoteGetPricing,
};
type Step2BasicDetailsProps = {
    contentId: string;
};
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & Step2BasicDetailsProps;

const blankSection = css`
    padding: 0;
    background-color: transparent;
`;

export const Step2Quotation: React.FC<Props> = ({
    SetEmailQuote,
    SetQuoteMainDetails,
    GetQuoteGetPricing,
    quantityRegular,
    quantityPremium,
    contentId,
    postcode,
    deliveryCheapest,
    deliverySoonest,
    productRegular,
    productPremium,
    hasAttempted,
    formState,
}) => {
    // wait for slider to have stopped moving, and delay by half a second to not overload calls to realtime pricing
    const [shouldSliderUpdate, setShouldSliderUpdate] = useState(true);
    // set loading state for when real time pricing is being requested
    const [isUpdatingRealTime, setIsUpdatingRealTime] = useState(false);
    const [fuelError, setFuelError] = useState("");

    const chosenFuel = formState.selectedProduct === AffinityProduct.Regular ? quantityRegular : quantityPremium;
    const chosenProduct = formState.selectedProduct === AffinityProduct.Regular ? productRegular : productPremium;

    const [quantityVal, setQuantityVal] = useState(chosenProduct?.minimumOrderQty || 0);

    const [formData, setFormData] = useState({
        selectedDelivery: formState.selectedDelivery ?? AffinityDelivery.Cheapest,
        quantity: quantityVal,
    });
    const [sendingQuoteReq, setSendingQuoteReq] = useState(false);
    // todo:  may want to move to constants?
    const optionTitles = {
        [AffinityDelivery.Cheapest]: {
            title: `Delivery by ${toDate(deliveryCheapest?.deliveryBy)}`,
            badge: "Cheapest deal",
        },
        [AffinityDelivery.Fastest]: {
            title: `Delivery by ${toDate(deliverySoonest?.deliveryBy)}`,
            badge: "Get it sooner",
        },
    };
    function handleSubmit() {
        if (!isUpdatingRealTime) {
            SetQuoteMainDetails({ ...formData, postcode: postcode, product: formState.selectedProduct });
        }
    }

    useEffect(() => {
        if (shouldSliderUpdate) {
            setIsUpdatingRealTime(true);
            GetQuoteGetPricing({
                selectedQuantity: quantityVal,
                selectedProduct: chosenProduct,
                postcode,
            });
        }
    }, [shouldSliderUpdate]);

    useEffect(() => {
        setIsUpdatingRealTime(false);
        if ((typeof deliveryCheapest === "undefined" || typeof deliverySoonest === "undefined") && hasAttempted) {
            setFuelError("There was an error getting your price, please refresh and try again");
        } else {
            setFuelError("");
        }
    }, [deliveryCheapest, deliverySoonest, hasAttempted]);

    const valueChange = (val: number) => {
        const newFormData = {
            ...formData,
            quantity: val,
        };
        setFormData(newFormData);
        setQuantityVal(val);
        setShouldSliderUpdate(false);
        if (timeOutTrack) {
            clearTimeout(timeOutTrack);
        }
        timeOutTrack = window.setTimeout(() => {
            setShouldSliderUpdate(true);
        }, 500);
    };

    const toasterContext = useContext(ToasterContext);

    const emailQuote = () => {
        const whichDelivery = formData.selectedDelivery;
        let netCost = 0;
        let netUnitPrice = 0;
        const productId = chosenProduct?.id || "";
        const productDescription = chosenProduct?.productTitle || "";
        const quoteOption = `${optionTitles[whichDelivery].badge}: ${optionTitles[whichDelivery].title}`;
        if (whichDelivery === AffinityDelivery.Cheapest) {
            netCost = deliveryCheapest?.netCost || 0;
            netUnitPrice = deliveryCheapest?.netUnitPrice || 0;
        } else {
            netCost = deliverySoonest?.netCost || 0;
            netUnitPrice = deliverySoonest?.netUnitPrice || 0;
        }
        setSendingQuoteReq(true);
        SetEmailQuote({
            productId,
            productDescription,
            email: formState.emailAddress,
            postcode,
            quantity: formData.quantity,
            quoteOption,
            netCost,
            netUnitPrice,
            onComplete: resp => {
                // todo: confirm with user that request was succesfull
                const { result } = { ...resp };
                if (result) {
                    // empty object passed as a good result.
                    const keys = Object.keys(result);
                    if (keys.length > 0) {
                        // todo: further checks may be needed.
                        toasterContext.addToast({ body: `Quote could not be sent`, messageType: "danger" });
                    } else {
                        toasterContext.addToast({
                            body: `Quote sent to ${formState.emailAddress}`,
                            messageType: "success",
                        });
                    }
                }
                setSendingQuoteReq(false);
            },
            onError: props => {
                let errorMessage = `Couldn't send quote to ${formState.emailAddress}, please try again or use a different email address.`;
                if (props.errorMessage) {
                    errorMessage = props.errorMessage;
                }
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <Section shouldFocus={true}>
                <Typography variant="h3">Select Quantity</Typography>
                <OrderQuantity
                    product="Regular"
                    min={chosenFuel?.minimum ?? 0}
                    max={chosenFuel?.maximum ?? 0}
                    buttonStep={500}
                    sliderStep={chosenFuel?.increment ?? 0}
                    onClick={(val: number) => valueChange(val)}
                    value={formData.quantity}
                />
            </Section>
            <Section>
                <Typography variant="h3">Choose an option</Typography>
                <RadioGroup
                    value={formData.selectedDelivery.toString()}
                    name="selectedDelivery"
                    onChangeHandler={e => {
                        const newFormData = {
                            ...formData,
                            selectedDelivery:
                                e.target.value === "1" ? AffinityDelivery.Cheapest : AffinityDelivery.Fastest,
                        };
                        setFormData(newFormData);
                    }}
                    css={css`
                        legend {
                            display: none;
                        }
                    `}
                >
                    <DeliveryCards
                        isLoading={isUpdatingRealTime}
                        deliveryCheapest={deliveryCheapest}
                        deliverySoonest={deliverySoonest}
                        errorMessage={fuelError}
                    />
                </RadioGroup>
                <Divider />
                <FlexWrapEnd>
                    <ButtonLink
                        href=""
                        icon={{ src: "Mail", position: "left" }}
                        mergeCss
                        css={textIconButton}
                        onClick={emailQuote}
                        disabled={
                            !(
                                formState.emailAddress.length > 0 &&
                                postcode.length > 0 &&
                                formData.quantity > 0 &&
                                deliveryCheapest &&
                                deliverySoonest
                            ) ||
                            isUpdatingRealTime ||
                            sendingQuoteReq
                        }
                    >
                        {translate("Email this quote to me")}
                    </ButtonLink>
                    <Button
                        type="submit"
                        variant="primary"
                        sizeVariant="large"
                        icon={{ position: "right", src: "ChevronRight" }}
                        css={css`
                            align-self: end;
                        `}
                        disabled={isUpdatingRealTime || !!fuelError}
                    >
                        {translate("Continue")}
                    </Button>
                </FlexWrapEnd>
            </Section>
            <ContentArea>
                <Zone contentId={contentId} zoneName="Step2ContentArea" requireRows={false} />
            </ContentArea>
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Step2Quotation);

const ContentArea = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${styleHelpers.rem(20)};
    padding: 0;
    z-index: 0;
    @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
        gap: ${styleHelpers.rem(30)};
    }
    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        gap: ${styleHelpers.rem(40)};
    }
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    gap: ${rem(30)};
`;
